import { PaginationProps } from '@/types';
import { useCallback, useMemo, useState } from 'react';

// Custom hook for optional controlled state through props [controlledValue, onChange]
// Otherwise controlled using useState [stateValue, setState]
function useControlledState<T>(
  controlledValue: T | undefined,
  defaultValue: T,
  onChange?: (value: T, ...args: unknown[]) => void
): [T, (value: T, ...args: unknown[]) => void] {
  const [stateValue, setState] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : stateValue;

  const set = useCallback(
    (stateToSet: T) => {
      if (typeof onChange === 'function') {
        onChange(stateToSet);
      }

      if (!isControlled) {
        setState(stateToSet);
      }
    },
    [onChange, isControlled]
  );

  return [value, set];
}

const calculateNumberOfPages = (numberOfItems: number, pageSize: number) =>
  Math.max(1, Math.ceil(numberOfItems / pageSize));

// Don't allow page to be less than 1 or greater than numberOfPages
const clampPage = (page: number, numberOfPages: number) => Math.max(1, Math.min(numberOfPages, page));

const useTablePagination = (numberOfItems: number, pagination: PaginationProps
) => {
  if (numberOfItems < 0) {
    throw new Error('numberOfItems must be non-negative');
  }
  
  if (pagination.defaultPageSize && pagination.defaultPageSize <= 0) {
    throw new Error('defaultPageSize must be positive');
  }

  const [pageSize, setPageSize] = useControlledState(
    pagination.pageSize,
    pagination && pagination.defaultPageSize ? pagination.defaultPageSize : 5,
    pagination.onPageSizeChange
  );
  const [unclampedPage, setPage] = useControlledState(
    pagination.page,
    pagination.defaultPage || 1,
    pagination.onPageChange
  );

  const numberOfPages = useMemo(() => calculateNumberOfPages(numberOfItems, pageSize), [numberOfItems, pageSize]);
  const page = useMemo(() => clampPage(unclampedPage, numberOfPages), [unclampedPage, numberOfPages]);
  const pageStart = (page - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, numberOfItems);

  return {
    page,
    pageStart,
    pageEnd,
    pageSize,
    numberOfPages,
    setPage,
    setPageSize,
  };
};

export { useTablePagination };
